<template>
  <div class="w-screen h-screen flex">
    <!-- Left Section -->
    <div class="left-section w-1/2 flex flex-col items-center justify-center p-10">
      <figure class="logo-container mb-8">
        <img src="@/assets/main-logo.png" alt="Logo" class="h-20" />
      </figure>
    </div>

    <!-- Right Section -->
    <div class="right-section w-1/2 bg-white flex items-center justify-center">
      <div class="bg-white shadow-lg rounded-xl p-8 max-w-md w-full border border-gray-200">
        <!-- Title -->
        <div class="text-xl font-semibold text-gray-800 mb-6 text-center">
          Dynamic Builder - Register
        </div>

        <!-- Email Input -->
        <div class="mb-4">
          <label class="block text-gray-700 text-sm font-medium mb-2">Email</label>
          <input
            class="custom-input custom-focus"
            type="email"
            v-model="email"
            placeholder="Enter your email"
            @keyup.enter="register"
          />
          <div v-if="emailError" class="text-red-600 text-sm mt-1">{{ emailError }}</div>
        </div>

        <!-- Password Input -->
        <div class="mb-4 relative">
          <label class="block text-gray-700 text-sm font-medium mb-2">Password</label>
          <input
            :type="showPassword ? 'text' : 'password'"
            class="custom-input custom-focus pr-10"
            v-model="password"
            placeholder="Enter your password"
            @keyup.enter="register"
          />
          <button
            type="button"
            class="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
            @click="togglePasswordVisibility"
          >
            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <div
            v-if="passwordError"
            class="text-red-600 text-sm mt-1"
          >{{ passwordError }}</div>
        </div>

        <!-- Confirm Password Input -->
        <div class="mb-4 relative">
          <label class="block text-gray-700 text-sm font-medium mb-2">Confirm Password</label>
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            class="custom-input custom-focus pr-10"
            v-model="confirmPassword"
            placeholder="Confirm your password"
            @keyup.enter="register"
          />
          <button
            type="button"
            class="absolute right-3 top-10 text-gray-500 hover:text-gray-700"
            @click="toggleConfirmPasswordVisibility"
          >
            <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <div
            v-if="confirmPasswordError"
            class="text-red-600 text-sm mt-1"
          >{{ confirmPasswordError }}</div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-message flex items-center justify-between p-3 mb-4">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle text-red-600 mr-2"></i>
            <span class="text-red-600 font-medium">{{ error }}</span>
          </div>
          <button @click="error = ''" class="text-red-600 hover:text-red-800">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Register Button -->
        <button
          @click="register"
          :disabled="!valid || isLoading"
          class="custom-button w-full flex items-center justify-center"
        >
          <span v-if="!isLoading">Register</span>
          <span v-else class="auth-loader"></span>
        </button>

        <!-- Login Link -->
        <div v-if="!isLoading" class="mt-4 text-sm text-center">
          Already have an account?
          <router-link to="login" class="register-text">
            Login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as authService from "../service/auth";

export default {
  name: "Register",
  data() {
    return {
      email: "",
      password: "",
      confirmPassword: "",
      error: "",
      emailError: "",
      passwordError: "",
      confirmPasswordError: "",
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
    };
  },
  computed: {
    valid() {
      return (
        this.email &&
        this.password &&
        this.confirmPassword &&
        !this.emailError &&
        !this.passwordError &&
        !this.confirmPasswordError
      );
    },
  },
  methods: {
    validateEmail() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.email && !emailRegex.test(this.email)) {
        this.emailError = "Please enter a valid email address.";
      } else {
        this.emailError = "";
      }
    },
    validatePassword() {
      this.validateConfirmPassword()
      if (this.password.length < 6) {
        this.passwordError = "Password must be at least 6 characters.";
      } else {
        this.passwordError = "";
      }
    },
    validateConfirmPassword() {
      if ( this.confirmPassword && this.password !== this.confirmPassword) {
        this.confirmPasswordError = "Passwords do not match.";
      } else {
        this.confirmPasswordError = "";
      }
    },
    async register() {
      this.validateEmail();
      this.validatePassword();
      this.validateConfirmPassword();
      if (this.emailError || this.passwordError || this.confirmPasswordError) {
        return;
      }
      this.isLoading = true; // Show loader
      this.error = ""; // Reset error message
      try {
        await authService.registerApi(this.email, this.password);
        // window.location.href = "/login"; // Redirect to login
        localStorage.setItem("pendingPassword", this.password);
        this.$router.push({ path: "/email-verification", query: { email: this.email } });
      } catch (err) {
        this.error = err.message || "Registration failed. Please try again.";
      } finally {
        this.isLoading = false; // Hide loader
      }
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
  },
  watch: {
    email() {
      this.validateEmail();
    },
    confirmPassword() {
      this.validateConfirmPassword();
    },
    password() {
      this.validatePassword();
    },
  },
};
</script>
